import React, { useState } from 'react';
import { Input, Button, Space, Card, Table, Empty, Select } from 'antd';
import { SearchOutlined, ClearOutlined, PrinterOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import PermissionGuard from '../../components/PermissionGuard';
import { Modules } from '../../constants/permissions';
import { usePW9312Query } from './hooks/usePW9312Query';
import type { AnomalyRow } from '../../services/pw9312Api';
import './PW9312Form.css';

const { Option } = Select;

const ANOMALY_TYPE_LABEL: Record<string, string> = {
  Q: '缺勤',
  V: '假別',
  P: '打卡',
  Z: '其他',
};

const DEPT_LEVEL_OPTIONS = [
  { value: 1, label: '1 - 部' },
  { value: 2, label: '2 - 處' },
  { value: 3, label: '3 - 課' },
  { value: 4, label: '4 - 組' },
];

const PW9312Form: React.FC = () => {
  const { loading, data, queried, noData, runQuery, reset } = usePW9312Query();

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deptFrom, setDeptFrom] = useState('');
  const [deptTo, setDeptTo] = useState('');
  const [gradeFrom, setGradeFrom] = useState('');
  const [gradeTo, setGradeTo] = useState('');
  const [deptLevel, setDeptLevel] = useState<number>(1);

  const handleQuery = () => {
    runQuery({
      dateFrom: dateFrom.trim() || undefined,
      dateTo: dateTo.trim() || undefined,
      deptFrom: deptFrom.trim() || undefined,
      deptTo: deptTo.trim() || undefined,
      gradeFrom: gradeFrom.trim() || undefined,
      gradeTo: gradeTo.trim() || undefined,
      deptLevel,
    });
  };

  const handleReset = () => {
    setDateFrom('');
    setDateTo('');
    setDeptFrom('');
    setDeptTo('');
    setGradeFrom('');
    setGradeTo('');
    setDeptLevel(1);
    reset();
  };

  const handlePrint = () => {
    if (!data || data.length === 0) return;
    const win = window.open('', '_blank');
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html><head><title>部門-日出勤異常表</title>
<style>body{font-family:'Microsoft JhengHei',sans-serif;margin:20px;font-size:12px}h2{text-align:center}table{width:100%;border-collapse:collapse}th,td{border:1px solid #333;padding:3px 6px}th{background:#f0f0f0;text-align:center}@media print{body{margin:10mm}}</style></head><body>
<h2>部門-日出勤異常表</h2>
<table><thead><tr><th>部門</th><th>員工編號</th><th>姓名</th><th>出勤日期</th><th>異常代碼</th><th>異常類型</th><th>備註</th><th>相關單號</th><th>職等</th></tr></thead><tbody>
${data.map(r => `<tr><td>${r.dept_name || r.F912_31}</td><td>${r.F912_01}</td><td>${r.F901_11}</td><td>${r.F912_02?.substring(0, 10) || ''}</td><td>${r.F912_03}</td><td>${ANOMALY_TYPE_LABEL[r.F912_11] || r.F912_11}</td><td>${r.F912_12}</td><td>${r.F912_13}</td><td>${r.F901_37}</td></tr>`).join('')}
</tbody></table></body></html>`);
    win.document.close();
    win.focus();
    win.print();
  };

  const columns: ColumnsType<AnomalyRow> = [
    { title: '部門', dataIndex: 'dept_name', key: 'dept_name', width: 120,
      render: (v: string, r: AnomalyRow) => v || r.F912_31 },
    { title: '員工編號', dataIndex: 'F912_01', key: 'F912_01', width: 100 },
    { title: '姓名', dataIndex: 'F901_11', key: 'F901_11', width: 100 },
    { title: '出勤日期', dataIndex: 'F912_02', key: 'F912_02', width: 110,
      render: (v: string) => v?.substring(0, 10) || '' },
    { title: '異常代碼', dataIndex: 'F912_03', key: 'F912_03', width: 100 },
    { title: '異常類型', dataIndex: 'F912_11', key: 'F912_11', width: 90,
      render: (v: string) => ANOMALY_TYPE_LABEL[v] || v },
    { title: '備註', dataIndex: 'F912_12', key: 'F912_12', ellipsis: true },
    { title: '相關單號', dataIndex: 'F912_13', key: 'F912_13', width: 120 },
    { title: '職等', dataIndex: 'F901_37', key: 'F901_37', width: 80 },
  ];

  return (
    <PermissionGuard module={Modules.PW9312} action="query">
      <div className="pw9312-container">
        <div className="pw9312-header">
          <h2>部門-日出勤異常表</h2>
          {queried && !noData && (
            <Button icon={<PrinterOutlined />} onClick={handlePrint}>列印</Button>
          )}
        </div>
        <Card size="small" style={{ marginBottom: 16 }}>
          <Space wrap size="middle">
            <div>
              <label className="pw9312-label">出勤日期起</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={e => setDateFrom(e.target.value)}
                style={{ width: 150 }}
              />
            </div>
            <div>
              <label className="pw9312-label">出勤日期迄</label>
              <Input
                type="date"
                value={dateTo}
                onChange={e => setDateTo(e.target.value)}
                style={{ width: 150 }}
              />
            </div>
            <div>
              <label className="pw9312-label">部門代碼起</label>
              <Input
                value={deptFrom}
                onChange={e => setDeptFrom(e.target.value)}
                placeholder="起始部門"
                style={{ width: 120 }}
                allowClear
                maxLength={4}
              />
            </div>
            <div>
              <label className="pw9312-label">部門代碼迄</label>
              <Input
                value={deptTo}
                onChange={e => setDeptTo(e.target.value)}
                placeholder="結束部門"
                style={{ width: 120 }}
                allowClear
                maxLength={4}
              />
            </div>
            <div>
              <label className="pw9312-label">職等起</label>
              <Input
                value={gradeFrom}
                onChange={e => setGradeFrom(e.target.value)}
                placeholder="起始職等"
                style={{ width: 100 }}
                allowClear
                maxLength={4}
              />
            </div>
            <div>
              <label className="pw9312-label">職等迄</label>
              <Input
                value={gradeTo}
                onChange={e => setGradeTo(e.target.value)}
                placeholder="結束職等"
                style={{ width: 100 }}
                allowClear
                maxLength={4}
              />
            </div>
            <div>
              <label className="pw9312-label">部門層級</label>
              <Select
                value={deptLevel}
                onChange={setDeptLevel}
                style={{ width: 120 }}
              >
                {DEPT_LEVEL_OPTIONS.map(o => (
                  <Option key={o.value} value={o.value}>{o.label}</Option>
                ))}
              </Select>
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', paddingTop: 18 }}>
              <Space>
                <Button type="primary" icon={<SearchOutlined />} onClick={handleQuery} loading={loading}>
                  查詢
                </Button>
                <Button icon={<ClearOutlined />} onClick={handleReset}>清除</Button>
              </Space>
            </div>
          </Space>
        </Card>
        {queried && noData && (
          <Empty description="查無此資料" style={{ marginTop: 40 }} />
        )}
        {queried && !noData && (
          <Table<AnomalyRow>
            columns={columns}
            dataSource={data}
            rowKey={(r, i) => `${r.F912_01}-${r.F912_02}-${i}`}
            loading={loading}
            size="small"
            pagination={{ pageSize: 50, showSizeChanger: true, showTotal: t => `共 ${t} 筆` }}
            scroll={{ x: 1000 }}
          />
        )}
      </div>
    </PermissionGuard>
  );
};

export default PW9312Form;
